import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Typography, Tag, Space, Table, Result, Button, Spin, Alert } from "antd";
import { ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import Loading from "../Loading";
import autorequestDetails, { AutorequestRequest } from "../../services/autorequestDetails";
import type { ColumnsType } from "antd/es/table";

const { Text, Title } = Typography;

/**
 * Formata uma data para exibição
 */
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return dateString;
  }
};

/**
 * Página para exibir detalhes das requests do Autorequest.
 */
const AutorequestDetailsContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [autorequestData, setAutorequestData] = useState<any>(null);
  const [requests, setRequests] = useState<AutorequestRequest[]>([]);
  const [error, setError] = useState<string | null>(null);

  /**
   * Carrega os dados do autorequest.
   */
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);

        const response = await autorequestDetails.getDetails(id);
        if (response.success && response.data) {
          setAutorequestData(response.data);
          setRequests(response.data.requests || []);
        } else {
          setError(response.message || "Autorequest não encontrado");
        }
      } catch (err: any) {
        console.error("Erro ao buscar dados:", err);
        setError(err.response?.data?.message || err.message || "Erro ao buscar dados");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  /**
   * Handler para voltar.
   */
  const handleBack = () => {
    window.close();
    if (!window.opener) {
      window.history.back();
    }
  };

  /**
   * Handler para recarregar dados.
   */
  const handleReload = () => {
    window.location.reload();
  };

  /**
   * Colunas da tabela de requests
   */
  const columns: ColumnsType<AutorequestRequest> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 300,
      render: (text: string) => <Text copyable code style={{ fontSize: "12px" }}>{text}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          success: "success",
          error: "error",
          warning: "warning",
          pending: "processing",
        };
        return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
      },
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 120,
    },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      width: 150,
    },
    {
      title: "Query",
      dataIndex: "query",
      key: "query",
      width: 120,
    },
    {
      title: "Court",
      dataIndex: "court",
      key: "court",
      width: 100,
    },
    {
      title: "Search",
      dataIndex: "search",
      key: "search",
      width: 100,
    },
    {
      title: "UF",
      dataIndex: "uf",
      key: "uf",
      width: 60,
    },
    {
      title: "Param",
      dataIndex: "param",
      key: "param",
      width: 200,
      render: (param: { key: string; value: string } | undefined) => {
        if (!param) return "-";
        return (
          <Space direction="vertical" size="small">
            <Text strong style={{ fontSize: "11px" }}>{param.key}:</Text>
            <Text copyable style={{ fontSize: "11px" }}>{param.value}</Text>
          </Space>
        );
      },
    },
    {
      title: "Criado em",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (date: string) => formatDate(date),
    },
    {
      title: "Respondido em",
      dataIndex: "respondedAt",
      key: "respondedAt",
      width: 180,
      render: (date: string) => formatDate(date),
    },
  ];

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Result
        status="error"
        title="Erro ao buscar dados"
        subTitle={<Text copyable>{error}</Text>}
        extra={[
          <Button type="primary" key="back" onClick={handleBack} icon={<ArrowLeftOutlined />}>
            Voltar
          </Button>,
          <Button key="retry" onClick={handleReload} icon={<ReloadOutlined />}>
            Tentar novamente
          </Button>,
        ]}
      />
    );
  }

  if (!autorequestData) {
    return (
      <Result
        status="404"
        title="Autorequest não encontrado"
        extra={
          <Button type="primary" onClick={handleBack} icon={<ArrowLeftOutlined />}>
            Voltar
          </Button>
        }
      />
    );
  }

  // Conta os status
  const statusCounts = {
    success: requests.filter((r) => r.status === "success").length,
    error: requests.filter((r) => r.status === "error").length,
    warning: requests.filter((r) => r.status === "warning").length,
    pending: requests.filter((r) => r.status === "pending" || !r.status).length,
  };

  return (
    <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Cabeçalho */}
          <div>
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <Text copyable code style={{ fontSize: "16px" }}>
                  {id}
                </Text>
                {autorequestData.key && (
                  <Tag color="blue" style={{ fontSize: "13px" }}>
                    {autorequestData.key}
                  </Tag>
                )}
                {autorequestData.value && (
                  <Text copyable style={{ fontSize: "13px" }}>
                    {autorequestData.value}
                  </Text>
                )}
              </div>
              {autorequestData.cnj && (
                <div>
                  <Text strong style={{ fontSize: "13px" }}>CNJ: </Text>
                  <Text copyable style={{ fontSize: "13px" }}>
                    {autorequestData.cnj}
                  </Text>
                </div>
              )}
              <Space wrap size="small">
                {statusCounts.error > 0 && (
                  <Tag color="error" style={{ fontSize: "12px" }}>Error: {statusCounts.error}</Tag>
                )}
                {statusCounts.warning > 0 && (
                  <Tag color="warning" style={{ fontSize: "12px" }}>Warning: {statusCounts.warning}</Tag>
                )}
                {statusCounts.success > 0 && (
                  <Tag color="success" style={{ fontSize: "12px" }}>Success: {statusCounts.success}</Tag>
                )}
                {statusCounts.pending > 0 && (
                  <Tag color="processing" style={{ fontSize: "12px" }}>Pending: {statusCounts.pending}</Tag>
                )}
              </Space>
            </Space>
          </div>

          {/* Tabela de Requests */}
          <div>
            <Title level={4}>Requests ({requests.length})</Title>
            <Table
              columns={columns}
              dataSource={requests}
              rowKey="id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total: ${total} requests`,
              }}
              scroll={{ x: 1500 }}
            />
          </div>
        </Space>
      </Card>
  );
};

export default AutorequestDetailsContent;
